import React from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const DataProcessingPage: React.FC = () => {
  const theme = useTheme();

  const sectionHeaderSx = {
    color: theme.palette.text.primary,
    mt: 5,
    mb: 2,
    fontWeight: 600,
  };

  const bodySx = {
    color: theme.palette.text.secondary,
    mb: 2,
    lineHeight: 1.8,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit" underline="hover">
            Home
          </Link>
          <Typography color="text.primary">Data Processing Agreement</Typography>
        </Breadcrumbs>

        <Typography variant="h3" sx={{ color: theme.palette.text.primary, mb: 1, fontWeight: 700 }}>
          Data Processing Agreement
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
          Summary of Terms — Article 28 GDPR
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
          Last updated: March 2026
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* Introduction */}
        <Typography variant="body1" sx={bodySx}>
          This page provides a summary of the key terms of the Data Processing Agreement (DPA)
          between AmudFin S.A. (the &ldquo;Processor&rdquo;) and our clients (the
          &ldquo;Controller&rdquo;). The full DPA, which forms part of the contractual agreement
          between AmudFin and each client, is available upon request. This summary is provided for
          transparency and does not replace the binding terms of the executed DPA.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 1. Roles */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          1. Roles and Responsibilities
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Under the GDPR, the processing of personal data through the AmudFin platform involves
          the following roles:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="Data Controller"
              secondary="The client organisation that subscribes to the AmudFin platform. The Controller determines the purposes and means of processing personal data (e.g., investor data, fund participant records, KYC information) through the Platform."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Data Processor"
              secondary="AmudFin S.A. processes personal data on behalf of and under the documented instructions of the Controller, solely to provide the Platform services described in the subscription agreement."
            />
          </ListItem>
        </List>
        <Typography variant="body1" sx={bodySx}>
          AmudFin does not determine the purposes of processing Client Data. We act exclusively on
          the Controller&apos;s documented instructions and do not process Client Data for our own
          purposes beyond what is necessary to provide the contracted services.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 2. Sub-processors */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          2. Sub-processors
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin engages a limited number of sub-processors to provide infrastructure services
          necessary for Platform operation. All sub-processors are bound by data processing
          agreements that impose obligations equivalent to those in our DPA with the Controller.
        </Typography>

        <TableContainer component={Paper} sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Service Provided</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Data Location</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Cloud infrastructure provider</TableCell>
                <TableCell>Virtual compute, storage, and networking for Platform hosting</TableCell>
                <TableCell>EU (Frankfurt, Amsterdam)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Database hosting provider</TableCell>
                <TableCell>Managed PostgreSQL database services with automated backups</TableCell>
                <TableCell>EU (Frankfurt)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Email delivery service</TableCell>
                <TableCell>Transactional email delivery (account notifications, alerts)</TableCell>
                <TableCell>EU (Ireland)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Monitoring and logging</TableCell>
                <TableCell>Application performance monitoring and error tracking (anonymised metadata only)</TableCell>
                <TableCell>EU (Germany)</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="body1" sx={bodySx}>
          The Controller will be notified in writing at least 30 days before any new sub-processor
          is engaged or any existing sub-processor is replaced. The Controller has the right to
          object to a new sub-processor on reasonable grounds related to data protection. If the
          objection cannot be resolved, the Controller may terminate the affected services without
          penalty.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 3. Data Processing Location */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          3. Data Processing Locations
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          All processing of personal data through the AmudFin platform occurs exclusively within
          the European Union and European Economic Area (EU/EEA). No personal data is transferred
          to, stored in, or accessed from countries outside the EU/EEA.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          In the event that a transfer outside the EU/EEA becomes necessary in the future (which
          is not currently planned), AmudFin will implement appropriate safeguards in accordance
          with Chapter V of the GDPR, including Standard Contractual Clauses (SCCs) adopted by the
          European Commission, supplemented by a transfer impact assessment. Any such transfer will
          require prior written approval from the Controller.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 4. Security Measures */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          4. Technical and Organisational Security Measures
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          In accordance with Article 32 GDPR, AmudFin implements the following security measures
          to protect personal data against unauthorised or unlawful processing, accidental loss,
          destruction, or damage:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText
              primary="Encryption"
              secondary="All data encrypted in transit using TLS 1.2 or higher. Data at rest encrypted using AES-256. Database connections secured with SSL/TLS."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Access control"
              secondary="Role-based access control (RBAC) with principle of least privilege. RS256 JWT authentication with mandatory two-factor authentication for all administrative functions. Four-eyes approval workflow for sensitive operations."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Audit logging"
              secondary="Comprehensive, immutable audit logs recording all data access, modifications, and administrative actions. Logs retained for 5 years in accordance with CSSF requirements."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Network security"
              secondary="Network segmentation, firewall rules restricting access to authorised sources only, and intrusion detection monitoring."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Backup and recovery"
              secondary="Automated daily backups with point-in-time recovery capability. Backups encrypted and stored in geographically separate EU data centres. Recovery procedures tested quarterly."
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Personnel security"
              secondary="Background checks for employees with access to production systems. Mandatory data protection training. Access revoked immediately upon role change or termination."
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 5. Breach Notification */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          5. Data Breach Notification
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          In the event of a personal data breach (as defined in Article 4(12) GDPR), AmudFin will:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Notify the Controller without undue delay, and in any event within 24 hours of becoming aware of the breach, enabling the Controller to meet the 72-hour notification obligation to the supervisory authority under Article 33 GDPR." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Provide the Controller with all information necessary to assess the nature and scope of the breach, including the categories and approximate number of data subjects affected, the categories of personal data records affected, and the likely consequences of the breach." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Describe the measures taken or proposed to address the breach, including measures to mitigate its possible adverse effects." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Cooperate fully with the Controller in investigating the breach, communicating with affected data subjects, and reporting to the competent supervisory authority (CNPD)." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Document the breach in an internal register, including the facts, effects, and remedial actions taken, in accordance with Article 33(5) GDPR." />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        {/* 6. Data Return and Deletion */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          6. Data Return and Deletion
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Upon termination or expiry of the subscription agreement, AmudFin will, at the
          Controller&apos;s election:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="Return all Client Data to the Controller in a structured, commonly used, machine-readable format (JSON or CSV), within 30 days of the termination date." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Securely delete all Client Data from production systems and active backups within 60 days of the termination date, using industry-standard data sanitisation methods." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Provide the Controller with written certification of deletion upon completion." />
          </ListItem>
        </List>
        <Typography variant="body1" sx={bodySx}>
          Data retained in archived backups will be deleted as part of the normal backup rotation
          cycle, no later than 90 days after termination. Any data subject to overriding legal
          retention obligations (such as AML records under Luxembourg law) will be retained for the
          minimum period required by law and then securely deleted.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 7. Audit Rights */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          7. Audit Rights
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The Controller has the right to audit AmudFin&apos;s compliance with the DPA and applicable
          data protection law. Audits may be conducted by the Controller or a qualified third-party
          auditor appointed by the Controller, subject to the following conditions:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="The Controller must provide at least 30 days' written notice of an intended audit." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Audits shall be conducted during normal business hours and shall not unreasonably disrupt Platform operations." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Third-party auditors must execute a non-disclosure agreement before accessing any AmudFin premises or systems." />
          </ListItem>
          <ListItem>
            <ListItemText primary="AmudFin will provide reasonable cooperation, including access to relevant documentation, personnel, and facilities." />
          </ListItem>
        </List>
        <Typography variant="body1" sx={bodySx}>
          AmudFin also makes available, upon request, the results of independent third-party
          security audits and certifications to demonstrate compliance with the security measures
          described in Section 4.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* 8. Requesting Full DPA */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          8. Requesting the Full Data Processing Agreement
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The full Data Processing Agreement is available to all current and prospective clients. To
          request a copy of the DPA for review or execution, please contact:
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>AmudFin S.A. — Legal Department</strong><br />
          Email: legal@amudfin.com<br />
          Data Protection Officer: dpo@amudfin.com
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          We will provide the DPA within 5 business days of receiving your request. The DPA is
          pre-signed by AmudFin and requires only the Controller&apos;s countersignature to become
          effective.
        </Typography>

        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            For questions about data processing or to report a data protection concern, contact our
            Data Protection Officer at dpo@amudfin.com.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default DataProcessingPage;
